import React, { useState, useCallback } from "react";
import {
  useLazyDownloadBulkUserTemplateQuery,
  useBulkUploadUsersMutation,
  type IBulkUploadUserResult,
} from "../../store/apis/user.api";
import GenericBulkUploadModal, {
  BulkUploadResultPayload,
} from "../common/GenericBulkUploadModal";
import toast from "react-hot-toast";
import { formatFileDateStamp } from "../../utils/timeUtils";

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const BulkUploadModal: React.FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [uploadResult, setUploadResult] = useState<BulkUploadResultPayload | null>(null);

  const [downloadTemplate, { isLoading: isDownloading }] =
    useLazyDownloadBulkUserTemplateQuery();
  const [bulkUploadUsers, { isLoading: isUploading }] =
    useBulkUploadUsersMutation();

  const handleDownloadTemplate = async () => {
    try {
      const blob = await downloadTemplate().unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStamp = formatFileDateStamp();
      link.download = `bulk_user_template_${dateStamp}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Bulk user upload template downloaded successfully.");
    } catch (error) {
      console.error("Download error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to download template";
      toast.error(errorMessage);
    }
  };

  const downloadErrorBlob = useCallback((blob: Blob) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    const dateStamp = formatFileDateStamp();
    link.download = `bulk_upload_errors_${dateStamp}.xlsx`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleFileUpload = async (file: File) => {
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append("file", file);

      const result: IBulkUploadUserResult = await bulkUploadUsers(formData).unwrap();

      if (result.errorFileBlob) {
        downloadErrorBlob(result.errorFileBlob);
      }

      setUploadResult({
        success: true,
        message: result.message || "Bulk upload completed",
        stats: {
          total: result.totalRows,
          success: result.created,
          failed: result.failed,
        },
        errorFileAction:
          result.failed > 0 && result.errorFileBlob
            ? {
                label: "Download Error Report (Excel)",
                description:
                  "An Excel file with failed rows and error annotations was downloaded automatically. You can also re-download it below.",
                onDownload: () => downloadErrorBlob(result.errorFileBlob!),
              }
            : undefined,
      });

      if (result.failed > 0) {
        toast.error(result.message);
      } else {
        toast.success(result.message);
      }
    } catch (error: unknown) {
      console.error("Upload error:", error);
      const err = error as { data?: { message?: string }; message?: string };
      const errorMessage =
        err?.data?.message || err?.message || "Upload failed";

      setUploadResult({
        success: false,
        message: errorMessage,
      });

      toast.error(errorMessage);
    }
  };

  const handleModalClose = () => {
    if (uploadResult) {
      onSuccess();
    }
    setUploadResult(null);
    onClose();
  };

  const instructions = [
    "Work Email must be unique across all employees.",
    "Employee IDs must be unique.",
    "Phone and Date of Birth are required for every row.",
    "Use template reference labels for Status, Role, Gender, Department, Designation, etc.",
    "Role is required and must exactly match a role name from the template list.",
    "If Employment Type or Emergency Relationship has a value, it must match the template reference values.",
    "The summary after upload describes what succeeded; fix failed rows in the error report and retry.",
  ];

  return (
    <GenericBulkUploadModal
      isOpen={isOpen}
      onClose={handleModalClose}
      title="Bulk Upload Employees"
      subtitle="Import multiple employee profiles at once using the formatted Excel template"
      acceptedExtensions={[".xlsx"]}
      fileTypeLabel="Excel Spreadsheet (.xlsx)"
      templateConfig={{
        description:
          "Excel template with headers, example row, and reference tables of allowed values.",
        buttonLabel: "Download Template",
        isDownloading,
        onDownload: handleDownloadTemplate,
      }}
      isUploading={isUploading}
      onUpload={handleFileUpload}
      instructions={instructions}
      result={uploadResult}
      onResetResult={() => setUploadResult(null)}
      onSuccessClose={onSuccess}
      width={720}
    />
  );
};

export default BulkUploadModal;
